const INSTAGRAM_HANDLE_RE = /^[A-Za-z0-9._]{3,30}$/;
const INSTAGRAM_HOST_RE = /^(?:www\.)?instagram\.com$/i;

const isValidInstagramHandle = handle => INSTAGRAM_HANDLE_RE.test(handle);

export const normalizeInstagramProfileUrl = value => {
  // A cleared form field arrives as undefined (final-form parses '' to undefined)
  // — treat it as empty so validation passes and the saved key gets cleared.
  if (value == null) {
    return '';
  }
  if (typeof value !== 'string') {
    return null;
  }

  const input = value.trim();
  if (!input) {
    return '';
  }

  if (input.startsWith('@')) {
    const handleFromAt = input.slice(1).trim();
    return isValidInstagramHandle(handleFromAt) ? `https://instagram.com/${handleFromAt}` : null;
  }

  // Allow plain handle format like "expert.handle" in addition to URL input.
  if (isValidInstagramHandle(input)) {
    return `https://instagram.com/${input}`;
  }

  const candidateUrl = /^https?:\/\//i.test(input) ? input : `https://${input}`;
  let parsedUrl;

  try {
    parsedUrl = new URL(candidateUrl);
  } catch (e) {
    return null;
  }

  if (!INSTAGRAM_HOST_RE.test(parsedUrl.hostname)) {
    return null;
  }

  const handle = parsedUrl.pathname.split('/').filter(Boolean)[0];
  if (!handle || !isValidInstagramHandle(handle)) {
    return null;
  }

  return `https://instagram.com/${handle}`;
};

export const isValidInstagramProfileInput = value => normalizeInstagramProfileUrl(value) !== null;

const TIKTOK_HANDLE_RE = /^[A-Za-z0-9._]{2,24}$/;
const TIKTOK_HOST_RE = /^(?:www\.)?tiktok\.com$/i;

const isValidTiktokHandle = handle => TIKTOK_HANDLE_RE.test(handle);

export const normalizeTiktokProfileUrl = value => {
  // A cleared form field arrives as undefined (final-form parses '' to undefined)
  // — treat it as empty so validation passes and the saved key gets cleared.
  if (value == null) {
    return '';
  }
  if (typeof value !== 'string') {
    return null;
  }

  const input = value.trim();
  if (!input) {
    return '';
  }

  if (input.startsWith('@')) {
    const handleFromAt = input.slice(1).trim();
    return isValidTiktokHandle(handleFromAt) ? `https://tiktok.com/@${handleFromAt}` : null;
  }

  // Allow plain handle format like "expert.handle" in addition to URL input.
  if (isValidTiktokHandle(input) && !TIKTOK_HOST_RE.test(input)) {
    return `https://tiktok.com/@${input}`;
  }

  const candidateUrl = /^https?:\/\//i.test(input) ? input : `https://${input}`;
  let parsedUrl;

  try {
    parsedUrl = new URL(candidateUrl);
  } catch (e) {
    return null;
  }

  if (!TIKTOK_HOST_RE.test(parsedUrl.hostname)) {
    return null;
  }

  const firstSegment = parsedUrl.pathname.split('/').filter(Boolean)[0] || '';
  const handle = firstSegment.startsWith('@') ? firstSegment.slice(1) : firstSegment;
  if (!handle || !isValidTiktokHandle(handle)) {
    return null;
  }

  return `https://tiktok.com/@${handle}`;
};

export const isValidTiktokProfileInput = value => normalizeTiktokProfileUrl(value) !== null;

const FACEBOOK_HANDLE_RE = /^[A-Za-z0-9.]{3,60}$/;
const FACEBOOK_HOST_RE = /^(?:www\.|m\.)?(?:facebook|fb)\.com$/i;

const isValidFacebookHandle = handle => FACEBOOK_HANDLE_RE.test(handle);

export const normalizeFacebookProfileUrl = value => {
  // A cleared form field arrives as undefined (final-form parses '' to undefined)
  // — treat it as empty so validation passes and the saved key gets cleared.
  if (value == null) {
    return '';
  }
  if (typeof value !== 'string') {
    return null;
  }

  const input = value.trim();
  if (!input) {
    return '';
  }

  if (input.startsWith('@')) {
    const handleFromAt = input.slice(1).trim();
    return isValidFacebookHandle(handleFromAt) ? `https://facebook.com/${handleFromAt}` : null;
  }

  // Allow plain page/profile name format in addition to URL input.
  if (isValidFacebookHandle(input) && !FACEBOOK_HOST_RE.test(input)) {
    return `https://facebook.com/${input}`;
  }

  const candidateUrl = /^https?:\/\//i.test(input) ? input : `https://${input}`;
  let parsedUrl;

  try {
    parsedUrl = new URL(candidateUrl);
  } catch (e) {
    return null;
  }

  if (!FACEBOOK_HOST_RE.test(parsedUrl.hostname)) {
    return null;
  }

  // Support numeric profile links like facebook.com/profile.php?id=123456789
  const firstSegment = parsedUrl.pathname.split('/').filter(Boolean)[0] || '';
  if (firstSegment === 'profile.php') {
    const profileId = parsedUrl.searchParams.get('id') || '';
    return /^\d+$/.test(profileId) ? `https://facebook.com/profile.php?id=${profileId}` : null;
  }

  const handle = firstSegment.startsWith('@') ? firstSegment.slice(1) : firstSegment;
  if (!handle || !isValidFacebookHandle(handle)) {
    return null;
  }

  return `https://facebook.com/${handle}`;
};

export const isValidFacebookProfileInput = value => normalizeFacebookProfileUrl(value) !== null;
